import { useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import { GlobalApi } from '../../../util/Global';
import { useContainer } from '../../../hooks';
import { Label } from '../Label';

export interface IMenuItem {
    value: number | string;
    label: string;
    color?: string;
    imageUrl?: string;
}

interface SelectProps {
    menuItems: IMenuItem[];
    placeholder?: string;
    isSearchable?: boolean;
    isError?: boolean;
    errorMessage?: string;
    label?: string;
    id?: string;
    iconSrc?: string;
    className?: string;
    selected: IMenuItem;
    onChange: (item: IMenuItem) => void;
    hideSelected?: boolean;
    isMenuAlignedToEnd?: boolean;
    noBorders?: boolean;
    hideArrow?: boolean;
    isRequired?: boolean;
}

export const Select = ({
    menuItems,
    placeholder,
    isSearchable,
    label,
    isError,
    errorMessage,
    iconSrc,
    className,
    selected,
    onChange,
    hideSelected,
    isMenuAlignedToEnd,
    noBorders,
    hideArrow,
    isRequired,
}: SelectProps) => {
    const [items, setItems] = useState<IMenuItem[]>([]);
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const globalApi: GlobalApi = useContainer(GlobalApi);

    const allItems = useRef<HTMLButtonElement[]>([]);
    const mainButtonRef = useRef<HTMLButtonElement>(null);
    const mainDivRef = useRef<HTMLDivElement>(null);

    const addItemToRef = (item: HTMLButtonElement | null) => {
        if (item) allItems.current.push(item);
    };

    useEffect(() => {
        setItems(menuItems);
    }, [menuItems]);

    useEffect(() => {
        setSearchValue(selected.label);
    }, [selected]);

    useEffect(() => {
        if (openMenu) {
            if (allItems.current.length > 10) {
                allItems.current.forEach(item => {
                    if (+item.id === selected.value) {
                        item.scrollIntoView();
                    }
                });
            } else if (allItems.current[allItems.current.length - 1].getBoundingClientRect().y + 30 > globalApi.innerHeight()) {
                allItems.current[allItems.current.length - 1].scrollIntoView();
            }
        }
    }, [openMenu]);

    const toggleMenu = () => {
        if (!openMenu) setOpenMenu(true);
        else {
            if (isSearchable && mainDivRef.current) mainDivRef.current.blur();
            else if (!isSearchable && mainButtonRef.current) mainButtonRef.current.blur();
        }
    };

    const changeSelect = (item: IMenuItem) => {
        onChange(item);
        if (isSearchable) setSearchValue(item.label);
        setOpenMenu(false);
    };

    const checkOnBlur = (e: React.FocusEvent<HTMLElement, Element>) => {
        if (!e.relatedTarget || !e.relatedTarget.classList.contains('select__option')) setOpenMenu(false);
    };

    const searchItems = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        if (value === '') setItems(menuItems);
        else {
            const searchedItems: IMenuItem[] = [];
            menuItems.forEach((item: IMenuItem) => {
                if (item.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())) {
                    searchedItems.push(item);
                }
            });
            setItems(searchedItems);
        }
    };

    const handleKeyDownChangeItem = (next: number) => {
        const newItem = items[next];
        onChange(newItem);
        setSearchValue(newItem.label);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const currentIndex = items.findIndex(item => item.value === selected.value);

        if (e.key === 'ArrowUp') {
            if (currentIndex === 0) {
                handleKeyDownChangeItem(items.length - 1);
            } else if (currentIndex > 0) {
                handleKeyDownChangeItem(currentIndex - 1);
            }
        }

        if (e.key === 'ArrowDown') {
            if (currentIndex === items.length - 1) {
                handleKeyDownChangeItem(0);
            } else if (currentIndex < items.length - 1) {
                handleKeyDownChangeItem(currentIndex + 1);
            }
        }
    };

    return (
        <div className={cc(['select', className])}>
            {label && <Label className="select__label paragraph-4" isRequired={isRequired} label={label} />}
            {isSearchable ? (
                <div
                    ref={mainDivRef}
                    tabIndex={0}
                    onClick={toggleMenu}
                    onBlur={checkOnBlur}
                    className={cc([
                        'select__field',
                        { 'no-padding-x': isSearchable },
                        { 'no-padding-left': isSearchable && !iconSrc },
                        { 'select__field--error': isError && errorMessage },
                        { 'select__field--no-background': noBorders },
                        { 'select__field--hide-arrow': hideArrow },
                        { 'select__field--open-menu': openMenu },
                    ])}
                >
                    <div className="select__container">
                        {iconSrc && <img className="select__icon" src={iconSrc} alt="Icon" />}
                        <input
                            className={cc(['select__search', { 'select__search--no-padding-left': iconSrc }])}
                            placeholder={placeholder}
                            type="text"
                            value={searchValue}
                            onChange={searchItems}
                            onFocus={() => setOpenMenu(true)}
                        />
                    </div>
                    {!openMenu && <img className="select__arrow" src="/img/arrow-down.svg" alt="Dropdown" />}
                </div>
            ) : (
                <button
                    ref={mainButtonRef}
                    onClick={toggleMenu}
                    onBlur={checkOnBlur}
                    onKeyDown={handleKeyDown}
                    type="button"
                    className={cc([
                        'select__field',
                        { 'select__field--error': isError && errorMessage },
                        { 'select__field--no-background': noBorders },
                        { 'select__field--hide-arrow': hideArrow },
                        { 'select__field--open-menu': openMenu },
                    ])}
                >
                    <div className="select__container">
                        {iconSrc && <img className="select__icon" src={iconSrc} alt="Icon" />}
                        {!hideSelected && selected.label}
                    </div>
                    {!openMenu && <img className="select__arrow" src="/img/arrow-down.svg" alt="Dropdown" />}
                </button>
            )}
            {isError && errorMessage && <p className="input__error-message">{errorMessage}</p>}
            {openMenu && (
                <div className={cc(['select__menu', { 'select__menu--align-end': isMenuAlignedToEnd }])}>
                    {items.map((item: IMenuItem) => (
                        <button
                            ref={el => addItemToRef(el)}
                            onClick={() => changeSelect(item)}
                            className={cc([
                                'select__option',
                                { 'select__option--active': selected.value === item.value },
                                { 'select__option--approved': item.color === 'approved' },
                                { 'select__option--rejected': item.color === 'rejected' },
                                { 'select__option--pending': item.color === 'pending' },
                            ])}
                            key={item.value}
                            type="button"
                            id={item.value.toString()}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
